import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  Avatar,
  Chip,
  Rating,
  Grid,
  Divider,
  IconButton,
  Snackbar
} from '@mui/material';
import {
  Verified,
  LocationOn,
  Security,
  Schedule,
  Message,
  CheckCircle,
  RequestQuote,
  Share
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import messagingService from '../../services/messagingService';
import { brand } from '../../theme/colors';
import { shareProfileLink } from '../../utils/shareLink';

/**
 * Simplified Verifier Card Component
 * Matches contractor card design for consistency
 * Focuses on essential information only
 */
const SimplifiedVerifierCard = ({ verifier, onHire, onViewProfile, onRequestQuote }) => {
  const navigate = useNavigate();

  // Safe data extraction with defaults
  const name = verifier.name || `${verifier.first_name || ''} ${verifier.last_name || ''}`.trim() || 'Verifier';
  const verified = verifier.verified || verifier.isVerified || verifier.verified_documents > 0;
  const avatar = verifier.avatar || verifier.profile_picture || verifier.profilePicture || '';
  const specialization = verifier.specialization || verifier.specialty || 'General Verification';
  const location = verifier.city || verifier.location || 'Zimbabwe';
  const rating = parseFloat(verifier.rating || verifier.average_rating || 0);
  const reviewCount = parseInt(verifier.reviews || verifier.review_count || verifier.total_reviews || 0);
  const completedVerifications = parseInt(verifier.completedVerifications || verifier.completed_verifications || verifier.total_verifications || 0);
  const onTimeRate = verifier.on_time_rate ?? verifier.onTimeRate ?? null;
  const responseRate = verifier.response_rate ?? verifier.responseRate ?? null;
  const trustScore = verifier.trust_score ?? verifier.trustScore ?? null;
  // New verifiers (no computed data yet) start at sensible baselines, like new contractors.
  const onTimeDisplay = onTimeRate ?? 0;
  const responseDisplay = responseRate ?? 0;
  const trustDisplay = trustScore ?? 25;
  const isProfessional = verifier.role === 'professional_verifier' || verifier.tier === 'professional';
  const bio = verifier.bio || verifier.description || verifier.profile_summary || `Professional verifier with ${Math.floor(completedVerifications / 10)} years of experience in ${specialization.toLowerCase()}.`;
  const specialties = verifier.specialties || verifier.skills || verifier.service_areas || [specialization];
  const availability = verifier.availability || (verifier.is_available ? 'Available' : 'Busy');
  
  const handleViewProfile = () => {
    if (onViewProfile) {
      onViewProfile(verifier);
    } else {
      navigate(`/verifiers/${verifier.id}`);
    }
  };

  const [shareToastOpen, setShareToastOpen] = useState(false);
  const handleShare = async () => {
    const result = await shareProfileLink({ path: `/share/verifier/${verifier.id}`, title: `${name} on Eqaya` });
    if (result === 'copied') setShareToastOpen(true);
  };

  const handleHire = () => {
    if (onHire) {
      onHire(verifier);
    }
  };
  
  const handleRequestQuote = () => {
    if (onRequestQuote) {
      onRequestQuote(verifier);
    }
  };

  const handleMessage = async () => {
    try {
      const res = await messagingService.getOrCreateConversation(verifier.id);
      const convId = res.data?.id;
      navigate(convId ? `/messaging?conversation=${convId}` : '/messaging');
    } catch {
      navigate('/messaging');
    }
  };

  return (
    <Card 
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          boxShadow: 4,
          transform: 'translateY(-4px)'
        }
      }}
    >
      <CardContent>
        {/* Header: Photo, Name, Rating */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              src={avatar}
              alt={name}
              sx={{ 
                width: 56, 
                height: 56,
                bgcolor: 'primary.main'
              }}
            >
              <Security />
            </Avatar>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6">{name}</Typography>
                {verified && (
                  <Verified color="primary" fontSize="small" />
                )}
              </Box>
              <Typography variant="body2" color="text.secondary">
                {specialization} • {completedVerifications} verifications
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Rating value={rating} precision={0.1} readOnly size="small" />
              <Typography variant="body2" fontWeight="bold">
                {rating.toFixed(1)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <IconButton
                size="small"
                aria-label={`Share ${name}'s profile link`}
                onClick={handleShare}
                sx={{ color: 'text.secondary' }}
              >
                <Share sx={{ fontSize: 18 }} />
              </IconButton>
              <Typography
                variant="body2"
                color="primary"
                sx={{
                  cursor: 'pointer',
                  fontWeight: 500,
                  '&:hover': { textDecoration: 'underline' }
                }}
                onClick={handleViewProfile}
              >
                View Profile →
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Bio/Description */}
        <Typography variant="body2" sx={{ mb: 2 }}>
          {bio}
        </Typography>

        {/* Specialties/Skills Chips */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          {(Array.isArray(specialties) ? specialties : [specialties]).slice(0, 3).map((specialty, index) => (
            <Chip
              key={index}
              label={specialty}
              size="small"
              variant="outlined"
            />
          ))}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Stats Grid - 4 columns matching contractor card */}
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="primary">
                {completedVerifications}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Verifications
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Chip
                label={`${onTimeDisplay}%`}
                size="small"
                color={onTimeRate == null ? 'default' : onTimeDisplay >= 80 ? 'success' : onTimeDisplay >= 60 ? 'warning' : 'error'}
              />
              <Typography variant="caption" color="text.secondary" display="block">
                On Time
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="primary">
                {responseDisplay}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Response
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="primary">
                {trustDisplay}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Trust Score
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Location and Availability */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Chip 
            icon={<LocationOn />} 
            label={location} 
            size="small" 
          />
          <Chip 
            icon={<CheckCircle />} 
            label={availability} 
            size="small" 
            color={availability === 'Available' ? 'success' : 'warning'}
          />
        </Box>

        {/* Action Buttons - Message + Request Quote matching contractor card */}
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Button
              fullWidth
              variant="outlined"
              size="small"
              startIcon={<Message />}
              onClick={handleMessage}
              sx={{
                borderColor: brand.primary,
                color: brand.primary,
                fontWeight: 600,
                '&:hover': {
                  borderColor: brand.primaryDark,
                  backgroundColor: 'rgba(13, 148, 136, 0.08)',
                },
                transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              Message
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              fullWidth
              variant="contained"
              size="small"
              startIcon={<RequestQuote />}
              onClick={handleRequestQuote}
              sx={{
                background: `linear-gradient(135deg, ${brand.primary} 0%, ${brand.primaryDark} 100%)`,
                color: 'white',
                fontWeight: 600,
                '&:hover': {
                  background: `linear-gradient(135deg, ${brand.primaryDark} 0%, ${brand.primary} 100%)`,
                },
                transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              Request Verification
            </Button>
          </Grid>
        </Grid>
      </CardContent>

      <Snackbar
        open={shareToastOpen}
        autoHideDuration={3000}
        onClose={() => setShareToastOpen(false)}
        message="Profile link copied — share it with anyone"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Card>
  );
};

export default SimplifiedVerifierCard;
